import AdminMfa from '../AdminMfa';
import { useEffect, useRef, useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";
import { defaultSettings, type SiteSettings } from "./settings";
import KurumsalBilgiler from "./components/KurumsalBilgiler";
import MarkaKimlik from "./components/MarkaKimlik";
import IletisimSosyal from "./components/IletisimSosyal";
import KayitUyelik from "./components/KayitUyelik";
import Bildirimler from "./components/Bildirimler";
import GuvenlikKvkk from "./components/GuvenlikKvkk";
import OdemeBanka from "./components/OdemeBanka";
import Gelismis from "./components/Gelismis";

const tabs = [
  { id: "kurumsal", label: "Kurumsal Bilgiler", icon: "ri-building-2-line" },
  { id: "marka", label: "Marka & Kimlik", icon: "ri-palette-line" },
  { id: "iletisim", label: "İletişim & Sosyal", icon: "ri-contacts-line" },
  { id: "kayit", label: "Kayıt & Üyelik", icon: "ri-user-add-line" },
  { id: "bildirim", label: "Bildirimler", icon: "ri-notification-3-line" },
  { id: "guvenlik", label: "Güvenlik & KVKK", icon: "ri-shield-check-line" },
  { id: "odeme", label: "Ödeme & Banka", icon: "ri-bank-card-line" },
  { id: "gelismis", label: "Gelişmiş", icon: "ri-settings-5-line" },
];

export default function AdminAyarlar() {
  const { settings, loading, saving, error, save, load } = useSiteSettingsContext();

  const [active, setActive] = useState("kurumsal");
  const [draft, setDraft] = useState<SiteSettings>(defaultSettings);
  const [savedSnapshot, setSavedSnapshot] = useState<SiteSettings>(defaultSettings);
  const [justSaved, setJustSaved] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDraft(settings);
    setSavedSnapshot(settings);
  }, [settings]);

  useEffect(() => {
    return () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    };
  }, []);

  const dirty = JSON.stringify(draft) !== JSON.stringify(savedSnapshot);

  function update(patch: Partial<SiteSettings>) {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  function handleReset() {
    setDraft(savedSnapshot);
  }

  async function handleSave() {
    const ok = await save(draft);
    if (ok) {
      setSavedSnapshot(draft);
      setJustSaved(true);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setJustSaved(false), 2500);
    }
  }

  const tabProps = { settings: draft, update };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <SectionHeader
          title="Sistem Ayarları"
          description="Platformun tamamını kontrol eden merkezi yapılandırma. Kurumsal kimlik, kayıt akışı, bildirimler, güvenlik ve bakım modu."
        />

        <div className="flex flex-wrap items-center gap-3">
          {justSaved ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 whitespace-nowrap">
              <i className="ri-checkbox-circle-line text-base" aria-hidden="true"></i>
              Kaydedildi
            </span>
          ) : (
            <span className="text-xs text-foreground-500 whitespace-nowrap">
              {loading ? "Ayarlar yükleniyor" : error ? "Ayarlar doğrulanamadı" : dirty ? "Kaydedilmemiş değişiklikler var" : "Kaydedilmiş ayarlar gösteriliyor"}
            </span>
          )}

          {dirty && (
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap text-foreground-700 border border-background-200 hover:border-accent-300 transition-colors"
            >
              Sıfırla
            </button>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving || loading || !!error}
            className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
          </button>
        </div>
      </div>

      <AdminMfa/>
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line" aria-hidden="true"></i>
          {error}
          <button type="button" onClick={load} className="ml-auto underline">Tekrar dene</button>
        </div>
      )}

      <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1 w-fit">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActive(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              active === t.id
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            <i className={`${t.icon} text-base`} aria-hidden="true"></i>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-background-200 bg-background-50 p-6 animate-pulse">
              <div className="h-4 w-40 bg-background-100 rounded"></div>
              <div className="mt-4 h-10 w-full bg-background-100 rounded"></div>
              <div className="mt-3 h-10 w-full bg-background-100 rounded"></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {active === "kurumsal" && <KurumsalBilgiler {...tabProps} />}
          {active === "marka" && <MarkaKimlik {...tabProps} />}
          {active === "iletisim" && <IletisimSosyal {...tabProps} />}
          {active === "kayit" && <KayitUyelik {...tabProps} />}
          {active === "bildirim" && <Bildirimler {...tabProps} />}
          {active === "guvenlik" && <GuvenlikKvkk {...tabProps} />}
          {active === "odeme" && <OdemeBanka {...tabProps} />}
          {active === "gelismis" && <Gelismis {...tabProps} />}
        </>
      )}
    </div>
  );
}
